import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Sidebar from "../../components/layout/Sidebar";
import api from "../../utils/api";
import toast from "react-hot-toast";

const statusColor = {
  paid:     { bg: "#f0fff4", text: "#276749", dot: "#48bb78" },
  pending:  { bg: "#fffbeb", text: "#744210", dot: "#f6ad55" },
  overdue:  { bg: "#fff5f5", text: "#742a2a", dot: "#fc8181" },
  disputed: { bg: "#faf5ff", text: "#553c9a", dot: "#b794f4" },
  cancelled:{ bg: "#f7fafc", text: "#4a5568", dot: "#a0aec0" },
};

const fmt = (n) => "GHS " + Number(n).toLocaleString("en-GH", { minimumFractionDigits: 2 });

// ─── Upload Modal ─────────────────────────────────────────────────────────────
const UploadModal = ({ onClose, onUploaded }) => {
  const [form, setForm] = useState({
    invoiceNumber: "", vendorName: "", totalAmount: "", subtotal: "",
    vatAmount: "", invoiceDate: "", status: "pending", type: "purchase",
    notes: "", vatRate: "15", isVatInvoice: "true",
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === "subtotal" || name === "vatRate") {
        const sub  = parseFloat(name === "subtotal" ? value : prev.subtotal) || 0;
        const rate = parseFloat(name === "vatRate"  ? value : prev.vatRate)  || 0;
        const vat  = parseFloat((sub * rate / 100).toFixed(2));
        updated.vatAmount    = vat.toString();
        updated.totalAmount  = (sub + vat).toFixed(2);
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please attach an invoice file"); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("invoice", file);
      Object.entries(form).forEach(([k, v]) => formData.append(k, v));
      await api.post("/invoices", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Invoice uploaded!");
      onUploaded();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Upload Invoice</h2>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>
        <form onSubmit={handleSubmit} style={styles.modalForm}>
          <div style={styles.dropZone} onClick={() => document.getElementById("fileInput").click()}>
            {file ? (
              <div style={styles.fileSelected}>
                <span>{file.type.includes("pdf") ? "📄" : "🖼"}</span>
                <span style={styles.fileName}>{file.name}</span>
                <span style={styles.fileSize}>({(file.size/1024).toFixed(0)} KB)</span>
              </div>
            ) : (
              <>
                <div style={styles.dropIcon}>↑</div>
                <div style={styles.dropText}>Click to upload PDF, JPG, or PNG</div>
                <div style={styles.dropSub}>Max 10MB</div>
              </>
            )}
            <input id="fileInput" type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display:"none" }} onChange={(e) => setFile(e.target.files[0])} />
          </div>

          <div style={styles.modalRow}>
            <div style={styles.field}>
              <label style={styles.label}>Invoice Number</label>
              <input style={styles.input} name="invoiceNumber" placeholder="INV-0042" value={form.invoiceNumber} onChange={handleChange} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Invoice Date *</label>
              <input style={styles.input} type="date" name="invoiceDate" value={form.invoiceDate} onChange={handleChange} required />
            </div>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Vendor Name *</label>
            <input style={styles.input} name="vendorName" placeholder="Supplier name" value={form.vendorName} onChange={handleChange} required />
          </div>
          <div style={styles.modalRow}>
            <div style={styles.field}>
              <label style={styles.label}>Subtotal (GHS) *</label>
              <input style={styles.input} type="number" name="subtotal" placeholder="0.00" value={form.subtotal} onChange={handleChange} required />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>VAT Rate</label>
              <select style={styles.input} name="vatRate" value={form.vatRate} onChange={handleChange}>
                {["0","3","15"].map(r => <option key={r} value={r}>{r}%</option>)}
              </select>
            </div>
          </div>
          <div style={styles.modalRow}>
            <div style={styles.field}>
              <label style={styles.label}>VAT Amount</label>
              <input style={{ ...styles.input, background:"#f7f8fc" }} type="number" name="vatAmount" value={form.vatAmount} readOnly />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Total Amount</label>
              <input style={{ ...styles.input, background:"#f7f8fc", fontWeight:"bold" }} type="number" name="totalAmount" value={form.totalAmount} readOnly />
            </div>
          </div>
          <div style={styles.modalRow}>
            <div style={styles.field}>
              <label style={styles.label}>Type</label>
              <select style={styles.input} name="type" value={form.type} onChange={handleChange}>
                {["purchase","sales","expense","utility","payroll","other"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Status</label>
              <select style={styles.input} name="status" value={form.status} onChange={handleChange}>
                {["pending","paid","overdue","disputed","cancelled"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>Cancel</button>
            <button type="submit" style={loading ? {...styles.submitBtn, opacity:0.7} : styles.submitBtn} disabled={loading}>
              {loading ? "Uploading..." : "Upload Invoice"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const InvoiceList = () => {
  const navigate = useNavigate();
  const [invoices,     setInvoices]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [totalPages,   setTotalPages]   = useState(1);
  const [search,       setSearch]       = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType,   setFilterType]   = useState("all");
  const [selected,     setSelected]     = useState([]);
  const [showModal,    setShowModal]    = useState(false);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search)                    params.append("search", search);
      if (filterStatus !== "all")    params.append("status", filterStatus);
      if (filterType   !== "all")    params.append("type",   filterType);
      const res = await api.get(`/invoices?${params}`);
      setInvoices(res.data.invoices);
      setTotal(res.data.total);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [page, search, filterStatus, filterType]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const toggleSelect  = (id) => setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const toggleAll     = () => setSelected(selected.length === invoices.length ? [] : invoices.map(i => i._id));

  const handleExport = () => {
    const selectedInvoices = invoices.filter(inv => selected.includes(inv._id));
    const headers = ["Invoice #","Vendor","Date","Amount","Status","Type"];
    const rows = selectedInvoices.map(inv => [
      inv.invoiceNumber, inv.vendor?.name,
      new Date(inv.invoiceDate).toLocaleDateString("en-GH"),
      inv.amount?.totalAmount, inv.status, inv.type,
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `invoices-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setSelected([]);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} invoice(s)? This cannot be undone.`)) return;
    try {
      await Promise.all(selected.map(id => api.delete(`/invoices/${id}`)));
      toast.success(`${selected.length} invoice(s) deleted`);
      setSelected([]);
      fetchInvoices();
    } catch (err) {
      toast.error("Delete failed — admin access required");
    }
  };

  return (
    <div style={styles.shell}>
      <Sidebar />
      <main style={styles.main}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.pageTitle}>Invoices</h1>
            <p style={styles.pageSub}>{total} total invoices</p>
          </div>
          <button onClick={() => setShowModal(true)} style={styles.uploadBtn}>+ Upload Invoice</button>
        </div>

        <div style={styles.filtersRow}>
          <div style={styles.searchWrap}>
            <span style={styles.searchIcon}>🔍</span>
            <input style={styles.searchInput} placeholder="Search by invoice #, vendor, or title..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
            {search && <button onClick={() => setSearch("")} style={styles.clearBtn}>✕</button>}
          </div>
          <select style={styles.select} value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="all">All Statuses</option>
            {["paid","pending","overdue","disputed","cancelled"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select style={styles.select} value={filterType} onChange={(e) => { setFilterType(e.target.value); setPage(1); }}>
            <option value="all">All Types</option>
            {["purchase","sales","expense","utility","payroll","other"].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          {selected.length > 0 && (
            <div style={styles.bulkActions}>
              <span style={styles.bulkCount}>{selected.length} selected</span>
              <button style={styles.bulkBtn} onClick={handleExport}>Export CSV</button>
              <button style={{ ...styles.bulkBtn, color: "#fc8181" }} onClick={handleDelete}>Delete</button>
            </div>
          )}
        </div>

        <div style={styles.tableCard}>
          {loading ? (
            <div style={styles.loadingWrap}>Loading invoices...</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr style={styles.thead}>
                  <th style={{ ...styles.th, width:"40px" }}>
                    <input type="checkbox" checked={selected.length === invoices.length && invoices.length > 0} onChange={toggleAll} />
                  </th>
                  <th style={styles.th}>Invoice #</th>
                  <th style={styles.th}>Title / Vendor</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>File</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr><td colSpan={8} style={styles.empty}>No invoices yet. Upload your first one!</td></tr>
                ) : invoices.map((inv, i) => {
                  const c = statusColor[inv.status] || statusColor.pending;
                  return (
                    <tr key={inv._id} style={{ ...(i % 2 === 0 ? styles.trEven : styles.trOdd), ...(selected.includes(inv._id) ? styles.trSelected : {}) }}
                      onClick={() => navigate(`/invoices/${inv._id}`)} css={{ cursor:"pointer" }}>
                      <td style={styles.td} onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.includes(inv._id)} onChange={() => toggleSelect(inv._id)} />
                      </td>
                      <td style={styles.td}><span style={styles.invNum}>{inv.invoiceNumber || "—"}</span></td>
                      <td style={styles.td}>
                        <div style={styles.titleCell}>{inv.title || "Untitled"}</div>
                        <div style={styles.vendorCell}>{inv.vendor?.name || "—"}</div>
                      </td>
                      <td style={styles.td}>{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString("en-GH") : "—"}</td>
                      <td style={styles.td}><span style={styles.typeTag}>{inv.type}</span></td>
                      <td style={styles.td}><strong>{fmt(inv.amount?.totalAmount || 0)}</strong></td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: c.bg, color: c.text }}>
                          <span style={{ ...styles.badgeDot, background: c.dot }} />
                          {inv.status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.fileTag}>{inv.file?.fileType?.toUpperCase() || "—"}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={styles.pagination}>
          <span style={styles.pageInfo}>Page {page} of {totalPages} · {total} invoices</span>
          <div style={styles.pageButtons}>
            <button style={styles.pageBtn} onClick={() => setPage(p => p - 1)} disabled={page === 1}>← Prev</button>
            <button style={{ ...styles.pageBtn, ...styles.pageBtnActive }}>{page}</button>
            <button style={styles.pageBtn} onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>Next →</button>
          </div>
        </div>
      </main>

      {showModal && <UploadModal onClose={() => setShowModal(false)} onUploaded={fetchInvoices} />}
    </div>
  );
};

const styles = {
  shell: { display:"flex", minHeight:"100vh", background:"#f7f8fc", fontFamily:"sans-serif" },
  main: { flex:1, padding:"36px 40px", overflowY:"auto" },
  header: { display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"24px" },
  pageTitle: { fontSize:"24px", fontWeight:"bold", color:"#0a0f1e", marginBottom:"4px" },
  pageSub: { fontSize:"13px", color:"#718096" },
  uploadBtn: { background:"#0a0f1e", color:"#fff", padding:"10px 20px", borderRadius:"8px", fontSize:"13px", fontWeight:"bold", border:"none", cursor:"pointer" },
  filtersRow: { display:"flex", alignItems:"center", gap:"12px", marginBottom:"20px", flexWrap:"wrap" },
  searchWrap: { flex:1, minWidth:"240px", display:"flex", alignItems:"center", background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:"8px", padding:"0 12px" },
  searchIcon: { fontSize:"14px", marginRight:"6px", color:"#a0aec0" },
  searchInput: { flex:1, border:"none", outline:"none", fontSize:"13px", padding:"10px 0", background:"transparent", color:"#0a0f1e" },
  clearBtn: { background:"none", border:"none", cursor:"pointer", color:"#a0aec0", fontSize:"13px" },
  select: { padding:"10px 12px", border:"1.5px solid #e2e8f0", borderRadius:"8px", fontSize:"13px", background:"#fff", color:"#2d3748", outline:"none" },
  bulkActions: { display:"flex", alignItems:"center", gap:"8px" },
  bulkCount: { fontSize:"13px", color:"#718096" },
  bulkBtn: { background:"#fff", border:"1px solid #e2e8f0", borderRadius:"6px", padding:"6px 12px", fontSize:"12px", cursor:"pointer", color:"#2d3748" },
  tableCard: { background:"#fff", borderRadius:"12px", border:"1px solid #e2e8f0", overflow:"hidden", marginBottom:"16px" },
  loadingWrap: { padding:"48px", textAlign:"center", color:"#a0aec0", fontSize:"14px" },
  table: { width:"100%", borderCollapse:"collapse" },
  thead: { background:"#f7f8fc" },
  th: { textAlign:"left", padding:"12px 16px", fontSize:"11px", color:"#a0aec0", textTransform:"uppercase", letterSpacing:"0.5px", fontWeight:"600", borderBottom:"1px solid #e2e8f0" },
  td: { padding:"14px 16px", fontSize:"13px", color:"#2d3748", verticalAlign:"middle", cursor:"pointer" },
  trEven: { background:"#fff" },
  trOdd: { background:"#fafafa" },
  trSelected: { background:"#fffbeb" },
  invNum: { fontFamily:"monospace", fontWeight:"bold", color:"#0a0f1e", fontSize:"13px" },
  titleCell: { fontWeight:"600", color:"#0a0f1e", fontSize:"13px" },
  vendorCell: { fontSize:"11px", color:"#a0aec0", marginTop:"2px" },
  typeTag: { background:"#edf2f7", color:"#4a5568", padding:"3px 8px", borderRadius:"4px", fontSize:"11px", textTransform:"capitalize" },
  badge: { display:"inline-flex", alignItems:"center", gap:"5px", padding:"4px 10px", borderRadius:"20px", fontSize:"11px", fontWeight:"600", textTransform:"capitalize" },
  badgeDot: { width:"6px", height:"6px", borderRadius:"50%" },
  fileTag: { background:"#ebf8ff", color:"#2b6cb0", padding:"3px 8px", borderRadius:"4px", fontSize:"10px", fontWeight:"bold", fontFamily:"monospace" },
  empty: { textAlign:"center", padding:"48px", color:"#a0aec0", fontSize:"14px" },
  pagination: { display:"flex", justifyContent:"space-between", alignItems:"center" },
  pageInfo: { fontSize:"13px", color:"#718096" },
  pageButtons: { display:"flex", gap:"6px" },
  pageBtn: { padding:"6px 12px", border:"1px solid #e2e8f0", borderRadius:"6px", background:"#fff", fontSize:"13px", cursor:"pointer", color:"#4a5568" },
  pageBtnActive: { background:"#0a0f1e", color:"#fff", border:"1px solid #0a0f1e" },
  overlay: { position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 },
  modal: { background:"#fff", borderRadius:"16px", width:"540px", maxHeight:"90vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" },
  modalHeader: { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"24px 28px", borderBottom:"1px solid #e2e8f0" },
  modalTitle: { fontSize:"18px", fontWeight:"bold", color:"#0a0f1e" },
  closeBtn: { background:"none", border:"none", fontSize:"18px", cursor:"pointer", color:"#a0aec0" },
  modalForm: { padding:"24px 28px", display:"flex", flexDirection:"column", gap:"16px" },
  dropZone: { border:"2px dashed #e2e8f0", borderRadius:"10px", padding:"28px", textAlign:"center", cursor:"pointer", background:"#fafafa" },
  dropIcon: { fontSize:"28px", color:"#a0aec0", marginBottom:"8px" },
  dropText: { fontSize:"14px", color:"#4a5568", fontWeight:"600" },
  dropSub: { fontSize:"12px", color:"#a0aec0", marginTop:"4px" },
  fileSelected: { display:"flex", alignItems:"center", justifyContent:"center", gap:"10px" },
  fileName: { fontSize:"14px", fontWeight:"600", color:"#0a0f1e" },
  fileSize: { fontSize:"12px", color:"#a0aec0" },
  modalRow: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" },
  field: { display:"flex", flexDirection:"column", gap:"5px" },
  label: { fontSize:"12px", fontWeight:"600", color:"#2d3748" },
  input: { padding:"10px 12px", border:"1.5px solid #e2e8f0", borderRadius:"8px", fontSize:"13px", fontFamily:"sans-serif", outline:"none", background:"#fff", color:"#0a0f1e" },
  modalFooter: { display:"flex", justifyContent:"flex-end", gap:"10px", paddingTop:"8px", borderTop:"1px solid #f0f0f0" },
  cancelBtn: { padding:"10px 20px", background:"#fff", border:"1px solid #e2e8f0", borderRadius:"8px", fontSize:"13px", cursor:"pointer", color:"#4a5568" },
  submitBtn: { padding:"10px 24px", background:"#0a0f1e", color:"#fff", border:"none", borderRadius:"8px", fontSize:"13px", fontWeight:"bold", cursor:"pointer" },
};

export default InvoiceList;